import React, { useEffect, useState } from 'react';

import { JRCButton } from 'components/JRCButton/JRCButton';
import { useIntl } from 'react-intl';
import { CSSEditorComponentWrapper } from 'components/Editors/components/Editor.style';

import styled from 'styled-components';
import { JRCModal } from 'components/JRCModal/templates/JRCModal';
import { JRCModalProps } from 'types';
import { EditorInput } from 'components/Editors/components/EditorInput';
import { JRCIconButton } from 'components/JRCIconButton/JRCIconButton';
import { EditorInputCheckbox } from 'components/Editors/components/EditorInputCheckbox';

import { BETA_JRCDragAndDrop } from 'components/Beta/BETA_JRCDragAndDrop/BETA_JRCDragAndDrop';
import { arraySwapElements } from 'utils/utils.array';
import { CSSWidgetEditorListOption } from '../styles';
import { JRCIcon } from 'components/JRCIcon/JRCIcon';
import { WidgetArticleSliderContent, jFileLittle } from 'jamespot-user-api';

import { WidgetSliderSlide } from './types';

import { EditorImageUpload } from 'components/Editors/components/EditorImageUpload';
import { EditorInputImage } from 'components/Editors/components/EditorInputImage';

export const CSSSlide = styled.div`
    display: flex;
    align-items: center;
    border-radius: 8px;
    box-shadow: 0 2px 2px 0px rgb(0 0 0 / 20%);
    margin-bottom: 8px;
`;

export const CSSSlideTitle = styled.div`
    display: flex;
    flex: 1;
`;

export const CSSScroll = styled.div`
    overflow-x: hidden;
    overflow-y: auto;
    margin-bottom: 20px;
`;

export const CSSFlexColumn = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
`;

export const JRCWidgetArticleSliderEditor = ({
    slides,
    loop,
    useDots,
    token,
    onChange,
    onSave,
}: WidgetArticleSliderContent & {
    token: string;
    onSave: (content: Partial<WidgetArticleSliderContent>) => void;
    onChange: (content: Partial<WidgetArticleSliderContent>, override?: boolean) => void;
}) => {
    const intl = useIntl();
    const [forceOpen, setForceOpen] = useState<boolean>(false);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);

    useEffect(() => {
        // Automatically add slide if slides is empty
        if (slides?.length === 0) {
            setIsDisabled(true);
            setForceOpen(true);
            onChange({ slides: slides !== undefined ? [...slides, { title: '', text: '' }] : [] });
        } else {
            setIsDisabled(false);
        }
    }, [onChange, slides]);

    return (
        <CSSFlexColumn>
            <CSSEditorComponentWrapper>
                <BETA_JRCDragAndDrop
                    elms={slides ?? []}
                    renderEl={(slide, index) => {
                        return (
                            <CSSSlide key={index}>
                                <CSSWidgetEditorListOption>
                                    <JRCIcon name="icon-fs-dw" color={'grey3'} />
                                </CSSWidgetEditorListOption>
                                <CSSSlideTitle>
                                    {index + 1}. {slide.title}
                                </CSSSlideTitle>
                                <EditorSlide
                                    forceOpen={forceOpen && index === 0}
                                    title={slide.title}
                                    text={slide.text}
                                    file={slide.file ?? undefined}
                                    onUploadStart={() => undefined}
                                    onUploadSuccess={(file) =>
                                        onChange({
                                            slides:
                                                slides !== undefined
                                                    ? slides.map((elSlide, key) =>
                                                          key === index ? { ...slide, file } : elSlide,
                                                      )
                                                    : [],
                                        })
                                    }
                                    token={token ?? undefined}
                                    onSave={(payload) =>
                                        onChange({
                                            slides:
                                                slides !== undefined
                                                    ? slides.map((elSlide, key) =>
                                                          key === index ? { ...slide, ...payload } : elSlide,
                                                      )
                                                    : [],
                                        })
                                    }
                                    onClickDelete={() => {
                                        onChange({
                                            slides:
                                                slides !== undefined ? slides.filter((_, key) => key !== index) : [],
                                        });
                                    }}
                                />
                            </CSSSlide>
                        );
                    }}
                    onDrag={(dragIndex, hoverIndex) => {
                        if (slides !== undefined && Array.isArray(slides)) {
                            onChange({ slides: arraySwapElements(slides, dragIndex, hoverIndex) });
                        }
                    }}
                />
                <JRCButton
                    onClick={() =>
                        onChange({ slides: slides !== undefined ? [...slides, { title: '', text: '' }] : [] })
                    }
                    variant={'outlined'}
                    minWidth={'100%'}>
                    {intl.formatMessage({ id: 'PANEL_Add_Slide' })}
                </JRCButton>
            </CSSEditorComponentWrapper>
            <CSSEditorComponentWrapper>
                <EditorInputCheckbox
                    label={''}
                    initialValue={loop ? ['loop'] : []}
                    options={[{ label: intl.formatMessage({ id: 'PANEL_Loop' }), value: 'loop' }]}
                    onChange={(values) => onChange({ loop: values[0] ? values[0] === 'loop' : false })}
                />
            </CSSEditorComponentWrapper>
            <CSSEditorComponentWrapper>
                <EditorInputCheckbox
                    label={''}
                    initialValue={useDots ? ['dot'] : []}
                    options={[{ label: intl.formatMessage({ id: 'PANEL_Use_Dots' }), value: 'dot' }]}
                    onChange={(values) => onChange({ useDots: values[0] ? values[0] === 'dot' : false })}
                />
            </CSSEditorComponentWrapper>
            <JRCButton onClick={() => onSave({})} color={'primary'} minWidth={'100%'} disabled={isDisabled}>
                {intl.formatMessage({ id: 'PANEL_Save_And_Close' })}
            </JRCButton>
        </CSSFlexColumn>
    );
};

export const EditorSlide = ({
    forceOpen,
    title,
    text,
    displayAs,
    backgroundSize,
    backgroundPosition,
    file,
    token,
    onUploadSuccess,
    onSave,
    onClickDelete,
}: {
    forceOpen?: boolean;
    file?: jFileLittle;
    token?: string;
    onUploadStart: () => void;
    onUploadSuccess: (response: jFileLittle) => void;
    onSave: (slide: Partial<WidgetSliderSlide>) => void;
    onClickDelete: () => void;
} & Pick<WidgetSliderSlide, 'title' | 'text' | 'displayAs' | 'backgroundSize' | 'backgroundPosition'>) => {
    const [open, setOpen] = useState<boolean>(false);
    const [slide, setSlide] = useState<Partial<WidgetSliderSlide>>({
        title,
        text,
        displayAs: displayAs ?? 'background',
        backgroundSize: backgroundSize ?? 'cover',
        backgroundPosition: backgroundPosition ?? 'center',
    });
    const intl = useIntl();

    const argsModal: JRCModalProps = {
        open: open,
        title: intl.formatMessage({ id: 'PANEL_Edit_Slide' }),
        portalId: 'react-modal',
        closeHandler: () => {
            setOpen(false);
        },
        enableClickAwayCloseModal: false,
    };

    React.useEffect(() => {
        if (forceOpen) {
            setOpen(true);
        }
    }, [forceOpen]);

    return (
        <>
            <JRCIconButton
                onClick={() => setOpen(true)}
                icon="icon-pen"
                color={'grey3'}
                tooltip={{ description: intl.formatMessage({ id: 'PANEL_Edit_Slide' }) }}
            />
            {token && (
                <EditorImageUpload token={token} initialFile={file} onUploadSuccess={onUploadSuccess}>
                    {(openCrop) => {
                        return (
                            <>
                                {file && (
                                    <JRCIconButton
                                        onClick={openCrop}
                                        icon="icon-image"
                                        color={'grey3'}
                                        tooltip={{ description: intl.formatMessage({ id: 'PANEL_Crop_Image' }) }}
                                    />
                                )}
                            </>
                        );
                    }}
                </EditorImageUpload>
            )}
            <DeleteWithPrompt title={intl.formatMessage({ id: 'PANEL_Remove_Slide' })} onClick={onClickDelete} />
            <JRCModal {...argsModal}>
                <CSSScroll>
                    <CSSEditorComponentWrapper>
                        <EditorInput
                            label={intl.formatMessage({ id: 'PANEL_Edit_Slide_Title' })}
                            initialValue={title}
                            onChange={(title) => setSlide({ ...slide, title })}
                        />
                    </CSSEditorComponentWrapper>
                    <CSSEditorComponentWrapper>
                        <EditorInput
                            label={intl.formatMessage({ id: 'PANEL_Edit_Slide_Description' })}
                            initialValue={text}
                            onChange={(text) => setSlide({ ...slide, text })}
                        />
                    </CSSEditorComponentWrapper>
                    {token !== undefined && (
                        <EditorInputImage token={token} initialFile={file} onUploadSuccess={onUploadSuccess} />
                    )}
                    <JRCButton
                        disabled={file ? false : true}
                        onClick={() => {
                            setOpen(false);
                            onSave(slide);
                        }}
                        color={'primary'}
                        minWidth={'100%'}>
                        {intl.formatMessage({ id: 'PANEL_Save_And_Close' })}
                    </JRCButton>
                </CSSScroll>
            </JRCModal>
        </>
    );
};

/**
 * TODO: STANDALONE COMPONENT
 */
export const DeleteWithPrompt = ({
    title,
    description = 'Sure?',
    onClick,
}: {
    title: string;
    description?: string;
    onClick: () => void;
}) => {
    const [open, setOpen] = React.useState<boolean>(false);
    const intl = useIntl();

    const argsModal: JRCModalProps = {
        open: open,
        title,
        portalId: 'react-modal',
        closeHandler: () => {
            setOpen(false);
        },
    };

    return (
        <>
            <JRCIconButton
                onClick={() => setOpen(true)}
                icon="icon-trash"
                color={'orange'}
                tooltip={{ description: title }}
            />
            <JRCModal {...argsModal}>
                {description}
                <JRCButton
                    onClick={() => {
                        setOpen(false);
                        onClick();
                    }}
                    color={'primary'}
                    minWidth={'100%'}>
                    {intl.formatMessage({ id: 'PANEL_Confirm' })}
                </JRCButton>
            </JRCModal>
        </>
    );
};
